import { CognitoUserSession } from '@investigator/shared/types';
import { Auth } from 'aws-amplify';
import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { CONSTANTS, getIdTokenSafely } from '../shared/constants';
import { useUserSession } from '../shared/hooks';
import { AuthContext } from '../shared/providers';
import { AuthState } from '../shared/types/auth-types';
import { isFatalAuthError } from '../shared/utils/authErrorUtils';

// Configuration constants
const USER_REFRESH_INTERVAL_MS = 60 * 1000; // 60 seconds
const TOKEN_EXPIRY_BUFFER_SEC = 60; // Refresh if token expires in 60 seconds
const AUTH_CHECK_TIMEOUT_MS = 5000; // 5 second timeout for auth checks

/**
 * Component that guards against invalid user states and maintains session integrity
 * Performs periodic session validation and handles role changes
 *
 * @component
 * @returns {null} This is a guard component that renders nothing
 */
export const UserExistenceGuard = () => {
  const { user, authState, setUser } = useContext(AuthContext);
  const location = useLocation();
  const { signOut } = useUserSession();

  // Refs to prevent concurrent operations
  const refreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef(0);
  const signOutInProgressRef = useRef(false);

  // Extracted for static checking in the dependency array
  const accessTokenExp = (
    user?.signInUserSession as
      | import('../shared/types/cognito-sso-types').CognitoUserSession
      | undefined
  )?.accessToken?.payload?.exp;

  useEffect(() => {
    // Skip if no user or not signed in
    if (
      !user ||
      typeof user.username !== 'string' ||
      !authState ||
      authState === AuthState.SignedOut
    ) {
      return;
    }

    // Check for role-change triggered logout
    const storedInitialRole = sessionStorage.getItem('initialUserRole');
    if (sessionStorage.getItem('roleChangeLogout') === 'true') {
      sessionStorage.removeItem('roleChangeLogout');
      return;
    }

    /**
     * Main async function to check user session and handle refreshes
     */
    (async () => {
      try {
        // Create timeout promise for the entire operation
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('User existence check timed out'));
          }, AUTH_CHECK_TIMEOUT_MS);
        });

        // Run checks with timeout protection
        await Promise.race([
          (async () => {
            // 1) Ensure we have a valid ID token
            await getIdTokenSafely();

            // 2) Check if we need to refresh the user
            const nowSec = Math.floor(Date.now() / 1000);
            const accessExp = Number(
              (
                user?.signInUserSession as
                  | import('../shared/types/cognito-sso-types').CognitoUserSession
                  | undefined
              )?.accessToken?.payload?.exp ?? 0,
            );
            const isAccessExpiring =
              !accessExp || accessExp - nowSec <= TOKEN_EXPIRY_BUFFER_SEC;

            const lastUserRefresh = Number(
              sessionStorage.getItem('lastUserRefresh') || 0,
            );
            const intervalElapsed =
              Date.now() - lastUserRefresh > USER_REFRESH_INTERVAL_MS;

            // Check if we should refresh (and not already refreshing)
            const shouldRefresh =
              (isAccessExpiring || intervalElapsed) &&
              !refreshingRef.current &&
              Date.now() - lastRefreshTimeRef.current > 5000; // Min 5s between refreshes

            if (shouldRefresh) {
              // Prevent duplicate refreshes
              refreshingRef.current = true;
              lastRefreshTimeRef.current = Date.now();

              // Update session storage optimistically
              sessionStorage.setItem('lastUserRefresh', String(Date.now()));

              try {
                // Refresh the user with timeout
                const refreshPromise = Auth.currentAuthenticatedUser({
                  bypassCache: true,
                });

                const cognitoUser = await Promise.race([
                  refreshPromise,
                  new Promise<never>((_, reject) => {
                    setTimeout(
                      () => reject(new Error('User refresh timeout')),
                      3000,
                    );
                  }),
                ]);

                if (cognitoUser && setUser) {
                  setUser(cognitoUser);
                }
              } catch (refreshError: Error | unknown) {
                console.warn(
                  '[UserExistenceGuard] User refresh failed:',
                  (refreshError as Error)?.message,
                );

                // Check if this is a fatal auth error that requires sign-out
                if (
                  isFatalAuthError(refreshError) &&
                  !signOutInProgressRef.current
                ) {
                  console.error(
                    '[UserExistenceGuard] Auth/refresh token error detected, forcing sign-out',
                  );
                  signOutInProgressRef.current = true;

                  // Clear session flags to prevent re-entrance
                  sessionStorage.removeItem('lastUserRefresh');

                  await signOut();
                  return;
                }
              } finally {
                refreshingRef.current = false;
              }
            }

            // 3) Check for role changes
            const groups = (
              user?.signInUserSession as CognitoUserSession | undefined
            )?.accessToken?.payload?.['cognito:groups'];

            const has = (g: string) =>
              Array.isArray(groups) && groups.includes(g);

            const currentRole = has(CONSTANTS.ROLE.admin)
              ? 'admin'
              : has(CONSTANTS.ROLE.analyst)
                ? 'analyst'
                : has(CONSTANTS.ROLE.viewer)
                  ? 'viewer'
                  : undefined;

            // Store initial role if not set
            if (!storedInitialRole && currentRole) {
              sessionStorage.setItem('initialUserRole', currentRole);
            }
            // Check for role change
            else if (
              storedInitialRole &&
              currentRole &&
              currentRole !== storedInitialRole
            ) {
              console.warn('[UserExistenceGuard] Role change detected', {
                from: storedInitialRole,
                to: currentRole,
              });

              // Mark for logout and clean up
              sessionStorage.setItem('roleChangeLogout', 'true');
              sessionStorage.removeItem('initialUserRole');

              if (!signOutInProgressRef.current) {
                signOutInProgressRef.current = true;
                await signOut();
              }
            }
          })(),
          timeoutPromise,
        ]);
      } catch (error: unknown) {
        const msg = (error as Error)?.message || '';

        console.error('[UserExistenceGuard] Error during checks:', msg);

        // Check if this is a fatal error that requires sign-out
        if (isFatalAuthError(error) && !signOutInProgressRef.current) {
          console.error(
            '[UserExistenceGuard] Fatal auth error, forcing sign-out',
          );
          signOutInProgressRef.current = true;

          // Clear session flags to prevent re-entrance
          sessionStorage.removeItem('lastUserRefresh');

          try {
            await signOut();
          } catch (signOutError) {
            console.error(
              '[UserExistenceGuard] Sign out failed:',
              signOutError,
            );
            // Force redirect as last resort
            window.location.href = '/login';
          }
        } else {
          // Unknown error - log but don't sign out to avoid flapping
          console.warn(
            '[UserExistenceGuard] Unknown error, continuing without action',
          );
        }
      }
    })();
  }, [
    location.pathname,
    user?.username,
    accessTokenExp,
    authState,
    signOut,
    setUser,
    user,
  ]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Reset refs on unmount
      refreshingRef.current = false;
      signOutInProgressRef.current = false;
      lastRefreshTimeRef.current = 0;
    };
  }, []);

  return null;
};
